import { useState, useMemo } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import type { SectorData, SectorLapSummary, SessionSummary } from '@/types/telemetry';
import { formatTime, formatDate } from '@/lib/format';
import { useI18n } from '@/context/I18nContext';
import { Pagination } from '@/components/common/Pagination';

interface LapStatsTableProps {
  sectors: SectorData[];
  sessions?: SessionSummary[];
  lapSummaries?: SectorLapSummary[];
  bestSectorTimesMs?: number[];
}

interface ProcessedLap {
  sessionId: string;
  lapNumber: number;
  sectorDurations: number[];
  totalTime: number;
  maxSpeedKmH: number;
  maxRpm: number;
  driverName: string;
  sessionDate: string;
}

type SortField = 'lapNumber' | 'driverName' | 'sessionDate' | 'totalTime' | 'maxSpeedKmH' | 'maxRpm' | `sector${number}`;
type SortDir = 'asc' | 'desc';

const PAGE_SIZE = 10;

export function LapStatsTable({ sectors, lapSummaries, bestSectorTimesMs }: LapStatsTableProps) {
  const { t } = useI18n();
  const [sortField, setSortField] = useState<SortField>('totalTime');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [page, setPage] = useState(1);

  const processedLaps: ProcessedLap[] = useMemo(() => {
    if (!lapSummaries || lapSummaries.length === 0) return [];
    return lapSummaries.map((l) => ({
      sessionId: l.sessionId,
      lapNumber: l.lapNumber,
      sectorDurations: l.sectorDurationsMs || [],
      totalTime: l.totalTimeMs,
      maxSpeedKmH: l.maxSpeedKmh,
      maxRpm: l.maxRpm,
      driverName: l.driverName || '—',
      sessionDate: l.sessionDate ? formatDate(l.sessionDate) : '—',
    }));
  }, [lapSummaries]);

  const bestTotalTime = useMemo(() => {
    const validTotalTimes = processedLaps.map(l => l.totalTime).filter(t => t > 0);
    return validTotalTimes.length > 0 ? Math.min(...validTotalTimes) : 0;
  }, [processedLaps]);

  const bestSectorTimes = bestSectorTimesMs || [];

  // Sort
  const sorted = useMemo(() => {
    const copy = [...processedLaps];
    copy.sort((a, b) => {
      let aVal: number | string;
      let bVal: number | string;

      if (sortField.startsWith('sector')) {
        const sIdx = parseInt(sortField.replace('sector', ''), 10);
        aVal = a.sectorDurations[sIdx] || 0;
        bVal = b.sectorDurations[sIdx] || 0;
      } else if (sortField === 'driverName' || sortField === 'sessionDate') {
        aVal = a[sortField];
        bVal = b[sortField];
      } else {
        aVal = a[sortField] as number;
        bVal = b[sortField] as number;
      }

      // Put zero/empty at the bottom
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        if (aVal <= 0) aVal = Infinity;
        if (bVal <= 0) bVal = Infinity;
        return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
      }

      // String comparison
      const cmp = String(aVal).localeCompare(String(bVal));
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [processedLaps, sortField, sortDir]);

  // Paginate
  const totalPages = Math.ceil(sorted.length / PAGE_SIZE);
  const currentPage = Math.min(page, totalPages || 1);
  const paginatedLaps = sorted.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  function toggleSort(field: SortField) {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
    setPage(1);
  }

  function SortIcon({ field }: { field: SortField }) {
    if (sortField !== field) return null;
    return sortDir === 'asc'
      ? <ChevronUp size={12} className="inline ml-0.5" />
      : <ChevronDown size={12} className="inline ml-0.5" />;
  }

  if (processedLaps.length === 0) {
    return (
      <div className="card p-6 text-center text-text-muted text-sm">
        {t('common.noData')}
      </div>
    );
  }

  const thBase = 'py-2.5 px-3 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted cursor-pointer select-none hover:text-text-secondary transition-colors';

  return (
    <div className="card p-0 overflow-hidden">
      <div className="flex items-center justify-between px-4 pt-4 sm:px-5 sm:pt-5 pb-4 border-b border-border-subtle">
        <span className="text-xs font-semibold uppercase tracking-widest text-text-secondary">
          {t('sector.lapStats')}
        </span>
        <span className="text-xs text-text-muted mono">
          {sorted.length} {sorted.length === 1 ? 'lap' : 'laps'}
        </span>
      </div>

      <div className="w-full overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="divider">
              <th className="text-center py-2.5 px-3 font-semibold text-[0.7rem] uppercase tracking-wider text-text-muted w-12 sticky left-0 bg-bg-surface z-10 border-r border-border-subtle">
                #
              </th>
              <th className={`text-left ${thBase}`} onClick={() => toggleSort('driverName')}>
                {t('sector.lapStatsDriver')}<SortIcon field="driverName" />
              </th>
              <th className={`text-left ${thBase}`} onClick={() => toggleSort('sessionDate')}>
                {t('sectorPanel.session')}<SortIcon field="sessionDate" />
              </th>
              <th className={`text-center ${thBase}`} onClick={() => toggleSort('lapNumber')}>
                {t('sectorPanel.lap')}<SortIcon field="lapNumber" />
              </th>
              {sectors.map((_, i) => (
                <th
                  key={i}
                  className={`text-right ${thBase}`}
                  onClick={() => toggleSort(`sector${i}` as SortField)}
                >
                  {t('sector.sectorN', { n: i + 1 })}<SortIcon field={`sector${i}` as SortField} />
                </th>
              ))}
              <th className={`text-right ${thBase}`} onClick={() => toggleSort('totalTime')}>
                {t('sector.lapStatsTotal')}<SortIcon field="totalTime" />
              </th>
              <th className={`text-right ${thBase}`} onClick={() => toggleSort('maxSpeedKmH')}>
                {t('sectorPanel.topSpeed')}<SortIcon field="maxSpeedKmH" />
              </th>
              <th className={`text-right ${thBase}`} onClick={() => toggleSort('maxRpm')}>
                {t('sectorPanel.maxRpm')}<SortIcon field="maxRpm" />
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedLaps.map((lap, i) => {
              const isBestTotal = bestTotalTime > 0 && lap.totalTime === bestTotalTime;
              const rank = (currentPage - 1) * PAGE_SIZE + i + 1;

              return (
                <tr
                  key={`${lap.sessionId}-${lap.lapNumber}`}
                  className={`hover:bg-bg-hover transition-colors ${
                    i < paginatedLaps.length - 1 ? 'divider' : ''
                  } ${
                    isBestTotal ? 'bg-yellow/10' : ''
                  }`}
                >
                  <td className="py-2.5 px-3 text-center mono font-medium text-text-secondary sticky left-0 bg-bg-surface z-10 border-r border-border-subtle">
                    {isBestTotal ? (
                      <span className="text-yellow font-bold text-xs">{rank}</span>
                    ) : (
                      <span className="text-xs text-text-muted">{rank}</span>
                    )}
                  </td>
                  <td className="py-2.5 px-3 text-text-primary font-medium">
                    {lap.driverName}
                  </td>
                  <td className="py-2.5 px-3 mono text-text-secondary text-xs">
                    {lap.sessionDate}
                  </td>
                  <td className="py-2.5 px-3 text-center mono text-text-secondary">
                    L{lap.lapNumber}
                  </td>
                  {lap.sectorDurations.map((dur, sIdx) => {
                    const isBestSector = bestSectorTimes[sIdx] > 0 && dur === bestSectorTimes[sIdx];
                    return (
                      <td
                        key={sIdx}
                        className={`py-2.5 px-3 mono text-right font-medium ${
                          isBestSector ? 'text-green font-semibold' : 'text-text-primary'
                        }`}
                      >
                        {formatTime(dur)}
                      </td>
                    );
                  })}
                  <td
                    className={`py-2.5 px-3 mono text-right font-semibold ${
                      isBestTotal ? 'text-green' : 'text-text-primary'
                    }`}
                  >
                    {formatTime(lap.totalTime)}
                  </td>
                  <td className="py-2.5 px-3 mono text-right text-text-primary font-medium">
                    {lap.maxSpeedKmH > 0 ? lap.maxSpeedKmH.toFixed(1) : '—'}
                  </td>
                  <td className="py-2.5 px-3 mono text-right text-text-primary font-medium">
                    {lap.maxRpm > 0 ? lap.maxRpm.toFixed(0) : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="px-4 pb-4">
          <Pagination page={currentPage} totalPages={totalPages} onPageChange={setPage} />
        </div>
      )}
    </div>
  );
}
